/**
 * DataQualityCategoryDrawer Component
 * ============================================================================
 * Right-side drawer showing detailed information about a Data Quality category.
 * Displays score, findings, educational content, sub-metrics, citations,
 * and recommendations.
 *
 * Pattern: Follows the RegulatoryBasisDrawer pattern from DC-1063.
 *
 * Related Files:
 * - client/types/dataQualityCategory.ts (type definitions)
 * - client/components/dataQuality/useDQCategoryDrawer.ts (state hook)
 * - client/components/evidence/RegulatoryBasisDrawer.tsx (pattern source)
 *
 * @version 1.0.0
 * @created 2026-01-28
 * ============================================================================
 */

import React, { useRef, useEffect, useState } from 'react';
import type {
  DataQualityCategoryDetail,
  CategoryFinding,
  SubMetricValue,
  CategoryRecommendation,
  DataGovernanceCitation,
} from '../../types/dataQualityCategory';
import {
  getRatingClass,
  getRatingLabel,
  getFindingClass,
  getFindingIcon,
  getSubMetricStatusClass,
  getPriorityClass,
} from '../../types/dataQualityCategory';
import { InternalCitationCard } from '../governance/InternalCitationCard';
import './data-quality-category-drawer.css';

// ============================================================================
// Types
// ============================================================================

export interface DataQualityCategoryDrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** The category detail to display */
  categoryDetail: DataQualityCategoryDetail | null;
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
}

// ============================================================================
// Component
// ============================================================================

export const DataQualityCategoryDrawer: React.FC<DataQualityCategoryDrawerProps> = ({
  isOpen,
  onClose,
  categoryDetail,
  loading = false,
  error = null,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [showAllCitations, setShowAllCitations] = useState(false);
  const [showAllRecommendations, setShowAllRecommendations] = useState(false);

  // ============================================================================
  // Effects
  // ============================================================================

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (isOpen && drawerRef.current) {
      drawerRef.current.focus();
    }
  }, [isOpen]);

  // Reset state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setShowAllCitations(false);
      setShowAllRecommendations(false);
    }
  }, [isOpen]);

  // ============================================================================
  // Render Helpers
  // ============================================================================

  const renderFinding = (finding: CategoryFinding, index: number) => (
    <li key={index} className={`dq-category__finding ${getFindingClass(finding.type)}`}>
      <span className="dq-category__finding-icon">{getFindingIcon(finding.type)}</span>
      <span className="dq-category__finding-message">{finding.message}</span>
      {finding.value !== undefined && (
        <span className="dq-category__finding-value">({finding.value})</span>
      )}
    </li>
  );

  const renderSubMetric = (metric: SubMetricValue, index: number) => (
    <div key={index} className={`dq-category__submetric ${getSubMetricStatusClass(metric.status)}`}>
      <div className="dq-category__submetric-header">
        <span className="dq-category__submetric-name">{metric.name}</span>
        <span className="dq-category__submetric-value">
          {metric.value !== null ? (
            <>
              {typeof metric.value === 'number' ? metric.value.toLocaleString() : metric.value}
              {metric.unit && <span className="dq-category__submetric-unit">{metric.unit}</span>}
            </>
          ) : (
            'N/A'
          )}
        </span>
      </div>
      {metric.details && (
        <p className="dq-category__submetric-details">{metric.details}</p>
      )}
    </div>
  );

  const renderCitation = (citation: DataGovernanceCitation, index: number) => (
    <div key={index} className="dq-category__citation">
      <div className="dq-category__citation-header">
        <span className="dq-category__citation-framework">[{citation.framework}]</span>
        <span className="dq-category__citation-document">{citation.document}</span>
      </div>
      <p className="dq-category__citation-section">{citation.section}</p>
      <blockquote className="dq-category__citation-text">
        "{citation.text}"
      </blockquote>
      {citation.url && (
        <a
          href={citation.url}
          target="_blank"
          rel="noopener noreferrer"
          className="dq-category__citation-link"
        >
          View Source Document
        </a>
      )}
    </div>
  );

  const renderRecommendation = (rec: CategoryRecommendation, index: number) => (
    <div key={index} className={`dq-category__recommendation ${getPriorityClass(rec.priority)}`}>
      <div className="dq-category__recommendation-header">
        <span className="dq-category__recommendation-priority">{rec.priority}</span>
        <span className="dq-category__recommendation-title">{rec.title}</span>
      </div>
      <p className="dq-category__recommendation-description">{rec.description}</p>
      {rec.impactEstimate !== undefined && (
        <span className="dq-category__recommendation-impact">
          Impact: {rec.impactEstimate}/10
        </span>
      )}
    </div>
  );

  // ============================================================================
  // Render
  // ============================================================================

  if (!isOpen) return null;

  const citations = categoryDetail?.citations || [];
  // DC-1670: framework citations render first (existing card), then the
  // customer's own governance under a "Your governance" subgroup. Collapse
  // governs the framework set only — internal citations are the differentiator
  // and stay visible so a user always learns their own governance is cited.
  const frameworkCitations = citations.filter(c => c.source !== 'internal');
  const internalCitations = citations.filter(c => c.source === 'internal');
  const visibleCitations = showAllCitations ? frameworkCitations : frameworkCitations.slice(0, 2);
  const hasMoreCitations = frameworkCitations.length > 2;

  const recommendations = categoryDetail?.recommendations || [];
  const visibleRecommendations = showAllRecommendations
    ? recommendations
    : recommendations.slice(0, 3);
  const hasMoreRecommendations = recommendations.length > 3;

  return (
    <>
      {/* Overlay */}
      <div
        className="dq-category__overlay"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="dq-category__drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dq-category-title"
        tabIndex={-1}
      >
        {/* Header */}
        <div className="dq-category__header">
          <div className="dq-category__header-content">
            <h2 id="dq-category-title" className="dq-category__title">
              Data Quality Component
            </h2>
            {categoryDetail && (
              <span className="dq-category__name">
                <span className="dq-category__icon">{categoryDetail.icon}</span>
                {categoryDetail.name}
              </span>
            )}
          </div>
          <button
            type="button"
            className="dq-category__close-btn"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="dq-category__content">
          {loading ? (
            <div className="dq-category__loading">
              <div className="dq-category__loading-spinner" />
              <p>Loading category details...</p>
            </div>
          ) : error ? (
            <div className="dq-category__error" role="alert">
              <p>{error}</p>
            </div>
          ) : categoryDetail ? (
            <>
              {/* Assessment Result Section */}
              <section className="dq-category__section">
                <h3 className="dq-category__section-title">Assessment Result</h3>
                <div className="dq-category__result">
                  <div className="dq-category__score-row">
                    <div className="dq-category__score">
                      <span className="dq-category__score-value">{categoryDetail.score}%</span>
                      <span className="dq-category__score-label">Score</span>
                    </div>
                    <span className={`dq-category__status ${getRatingClass(categoryDetail.rating)}`}>
                      {getRatingLabel(categoryDetail.rating)}
                    </span>
                  </div>
                  <p className="dq-category__result-summary">{categoryDetail.assessmentSummary}</p>
                </div>
              </section>

              {/* Key Findings Section */}
              {categoryDetail.findings.length > 0 && (
                <section className="dq-category__section">
                  <h3 className="dq-category__section-title">
                    Key Findings ({categoryDetail.findings.length})
                  </h3>
                  <ul className="dq-category__findings-list">
                    {categoryDetail.findings.map((finding, i) => renderFinding(finding, i))}
                  </ul>
                </section>
              )}

              {/* What This Measures Section */}
              <section className="dq-category__section">
                <h3 className="dq-category__section-title">What This Measures</h3>
                <div className="dq-category__definition">
                  <h4 className="dq-category__definition-name">{categoryDetail.name}</h4>
                  <p className="dq-category__definition-text">
                    {categoryDetail.whatItMeasures}
                  </p>
                </div>

                <div className="dq-category__rationale">
                  <h4>Why This Matters</h4>
                  <p>{categoryDetail.whyItMatters}</p>
                </div>

                {/* Score Thresholds */}
                <div className="dq-category__thresholds">
                  <h4>Score Thresholds</h4>
                  <div className="dq-category__thresholds-grid">
                    <div className="dq-category__threshold dq-category__threshold--excellent">
                      <span className="dq-category__threshold-label">Excellent</span>
                      <span className="dq-category__threshold-value">
                        ≥{categoryDetail.thresholds.excellent}%
                      </span>
                    </div>
                    <div className="dq-category__threshold dq-category__threshold--good">
                      <span className="dq-category__threshold-label">Good</span>
                      <span className="dq-category__threshold-value">
                        ≥{categoryDetail.thresholds.good}%
                      </span>
                    </div>
                    <div className="dq-category__threshold dq-category__threshold--poor">
                      <span className="dq-category__threshold-label">Poor</span>
                      <span className="dq-category__threshold-value">
                        ≥{categoryDetail.thresholds.poor}%
                      </span>
                    </div>
                    <div className="dq-category__threshold dq-category__threshold--critical">
                      <span className="dq-category__threshold-label">Critical</span>
                      <span className="dq-category__threshold-value">
                        &lt;{categoryDetail.thresholds.poor}%
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              {/* Sub-Metrics Breakdown Section */}
              {categoryDetail.subMetrics.length > 0 && (
                <section className="dq-category__section">
                  <h3 className="dq-category__section-title">Sub-Metrics Breakdown</h3>
                  <div className="dq-category__submetrics">
                    {categoryDetail.subMetrics.map((metric, i) => renderSubMetric(metric, i))}
                  </div>
                </section>
              )}

              {/* Data Governance Citations Section */}
              {citations.length > 0 && (
                <section className="dq-category__section">
                  <h3 className="dq-category__section-title">
                    Data Governance Citations ({citations.length})
                  </h3>
                  <div className="dq-category__citations">
                    {visibleCitations.map((citation, i) => renderCitation(citation, i))}
                  </div>
                  {hasMoreCitations && (
                    <button
                      className="dq-category__show-more"
                      onClick={() => setShowAllCitations(!showAllCitations)}
                    >
                      {showAllCitations
                        ? 'Show fewer citations'
                        : `Show ${frameworkCitations.length - 2} more citation${frameworkCitations.length - 2 !== 1 ? 's' : ''}`}
                    </button>
                  )}
                  {internalCitations.length > 0 && (
                    <>
                      <p className="ic-subgroup">Your governance</p>
                      <div className="dq-category__citations">
                        {internalCitations.map((citation, i) => (
                          <InternalCitationCard key={`internal-${i}`} citation={citation} />
                        ))}
                      </div>
                    </>
                  )}
                </section>
              )}

              {/* Recommendations Section */}
              {recommendations.length > 0 && (
                <section className="dq-category__section">
                  <h3 className="dq-category__section-title">Recommendations</h3>
                  <div className="dq-category__recommendations">
                    {visibleRecommendations.map((rec, i) => renderRecommendation(rec, i))}
                  </div>
                  {hasMoreRecommendations && (
                    <button
                      className="dq-category__show-more"
                      onClick={() => setShowAllRecommendations(!showAllRecommendations)}
                    >
                      {showAllRecommendations
                        ? 'Show fewer recommendations'
                        : `Show ${recommendations.length - 3} more`}
                    </button>
                  )}
                </section>
              )}

              {/* Not Analyzed Notice */}
              {!categoryDetail.wasAnalyzed && categoryDetail.notAnalyzedReason && (
                <section className="dq-category__section dq-category__section--notice">
                  <div className="dq-category__notice">
                    <span className="dq-category__notice-icon">ℹ️</span>
                    <p>{categoryDetail.notAnalyzedReason}</p>
                  </div>
                </section>
              )}
            </>
          ) : (
            <div className="dq-category__empty">
              <p>No category selected</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="dq-category__footer">
          <a
            href="/guides/data-quality"
            className="dq-category__footer-link"
          >
            View Data Quality Guide
          </a>
          <button
            type="button"
            className="dq-category__footer-btn"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
};

export default DataQualityCategoryDrawer;
